import { observer } from "mobx-react-lite";
import { CurlyBraces, DatabaseIcon } from "lucide-react";

import { JsonPathConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import Checkbox from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

const validationSchema = createBlockSchema<JsonPathConfiguration>()
  .requireName()
  .addRule(required("configuration.json_path", "JSON Path is required"))
  .addRule(required("configuration.source", "Source is required"))
  .build();

const JSONExtractionBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<JsonPathConfiguration>, "DefaultFields">) => {
    const block = useBlock<JsonPathConfiguration>();
    const isReadonly = useIsReadonly();

    const handleJsonPathChange = (path: string) => {
      block.setConfiguration({
        ...block.configuration,
        json_path: path,
        source: block.configuration?.source || "",
        return_first_match: block.configuration?.return_first_match ?? true,
      });
    };

    const handleReturnFirstMatchChange = (returnFirst: boolean) => {
      block.setConfiguration({
        ...block.configuration,
        json_path: block.configuration?.json_path || "$.*",
        source: block.configuration?.source || "",
        return_first_match: returnFirst,
      });
    };

    return (
      <>
        <SectionGroup
          title="JSON Path Configuration"
          description="Configure how to extract data from JSON documents"
          icon={<CurlyBraces className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="JSON Path" path="configuration.json_path">
              <Input
                className="w-full"
                value={block.configuration?.json_path || ""}
                onChange={e => handleJsonPathChange(e.target.value)}
                placeholder="$.*"
                disabled={isReadonly}
              />
            </ValidatedField>

            <ValidatedField name="Return First Match" path="">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="return-first-match"
                  label="Return First Match"
                  checked={block.configuration?.return_first_match ?? true}
                  onChange={e => handleReturnFirstMatchChange(e.target.checked)}
                  disabled={isReadonly}
                />
              </div>
            </ValidatedField>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the JSON source to extract data from"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<JsonPathConfiguration>(JSONExtractionBlockCore, {
  validationSchema,
});
